import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { CustomColorPickerInput } from '@air/component-custom-color-picker-input';
import { ChevronLeftIcon, ChevronRightIcon } from '@air/design-icons';
import { Popover } from '@air/primitive-popover';

import { ColorDropdownPickerOption } from './ColorDropdownPickerOption';
import { ColorDropdownPickerTrigger } from './ColorDropdownPickerTrigger';
import {
  type ColorDropdownPickerColor,
  type ColorDropdownPickerCopy,
  type ColorDropdownPickerGroup,
  type ColorDropdownPickerValue,
} from './colorDropdownPickerTypes';
import { normalizeColorDropdownPickerHex } from './normalizeColorDropdownPickerHex';

type ColorDropdownPickerView = 'custom' | 'groups';

export type ColorDropdownPickerProps = {
  colorGroups: readonly ColorDropdownPickerGroup[];
  copy: ColorDropdownPickerCopy;
  hex: string;
  isLoading: boolean;
  label: string | null;
  onChange: (color: ColorDropdownPickerValue) => void;
};

export const ColorDropdownPicker = memo(
  ({ colorGroups, copy, hex, isLoading, label, onChange }: ColorDropdownPickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<ColorDropdownPickerView>('groups');
    const stayOnCustomRef = useRef<boolean>(false);
    const hasGroupColors = colorGroups.some((group) => group.colors.length > 0);

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        setIsOpen(nextOpen);

        if (nextOpen) {
          stayOnCustomRef.current = false;
          setView(isLoading || !hasGroupColors ? 'custom' : 'groups');
        }
      },
      [hasGroupColors, isLoading],
    );

    const handleSelectGroupColor = useCallback(
      (color: ColorDropdownPickerColor) => {
        onChange({ hex: color.hex, label: color.label });
        setIsOpen(false);
      },
      [onChange],
    );

    const handleShowCustomColor = useCallback(() => {
      stayOnCustomRef.current = true;
      setView('custom');
    }, []);

    const handleShowGroupColors = useCallback(() => {
      setView('groups');
    }, []);

    const handleSaveCustomColor = useCallback(
      (nextHex: string) => {
        const normalized = normalizeColorDropdownPickerHex(nextHex);

        if (!normalized) {
          return;
        }

        onChange({ hex: normalized, label: null });
        setIsOpen(false);
      },
      [onChange],
    );

    useEffect(() => {
      if (!isOpen || isLoading || stayOnCustomRef.current || !hasGroupColors || view !== 'custom') {
        return;
      }

      setView('groups');
    }, [hasGroupColors, isLoading, isOpen, view]);

    const showGroupsView = !isLoading && view === 'groups' && hasGroupColors;

    return (
      <Popover
        align="start"
        onOpenChange={handleOpenChange}
        open={isOpen}
        side="bottom"
        size="large"
        trigger={<ColorDropdownPickerTrigger hex={hex} isOpen={isOpen} />}
      >
        <div className="flex w-[260px] flex-col gap-1">
          {showGroupsView ? (
            <>
              {hasGroupColors &&
                colorGroups.map((group) => (
                  <div key={group.id}>
                    <p className="px-2 py-1 font-semibold text-12 text-gray-11">{group.name}</p>
                    {group.colors.map((color) => (
                      <ColorDropdownPickerOption
                        color={color}
                        isSelected={color.hex === hex && color.label === label}
                        key={color.id}
                        onSelect={handleSelectGroupColor}
                      />
                    ))}
                  </div>
                ))}
              <hr className="h-px border-0 bg-gray-4" />
              <button
                className="flex min-h-9 w-full items-center gap-2 rounded-4 px-2 text-left text-12 text-gray-12 hover:bg-gray-3"
                data-testid="COLOR_DROPDOWN_PICKER_DRILL_IN"
                onClick={handleShowCustomColor}
                type="button"
              >
                <span
                  aria-hidden
                  className="size-4 shrink-0 rounded-4 border border-gray-6"
                  style={{ backgroundColor: hex }}
                />
                <span className="min-w-0 flex-1 font-medium">{copy.customColor}</span>
                <span className="shrink-0 font-mono text-10 text-gray-9 uppercase">{hex}</span>
                <ChevronRightIcon className="shrink-0 text-gray-9" size="sm" />
              </button>
            </>
          ) : (
            <>
              {!isLoading && hasGroupColors && (
                <button
                  className="flex min-h-9 w-full items-center gap-2 rounded-4 px-2 text-left font-medium text-12 text-gray-12 hover:bg-gray-3"
                  data-testid="COLOR_DROPDOWN_PICKER_BACK"
                  onClick={handleShowGroupColors}
                  type="button"
                >
                  <ChevronLeftIcon className="shrink-0 text-gray-9" size="sm" />
                  <span className="min-w-0 flex-1 truncate">{copy.groupsTitle}</span>
                </button>
              )}
              <CustomColorPickerInput
                defaultColor={hex}
                inputLabel={copy.customColorInputLabel}
                onSave={handleSaveCustomColor}
              />
            </>
          )}
        </div>
      </Popover>
    );
  },
);

ColorDropdownPicker.displayName = 'ColorDropdownPicker';
